'use client';

import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Spinner, Alert, Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router';
import { useData } from '../../../storage/accessors';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import Icon from '../../../components/Icon';
import { faArrowLeft, faChartPie, faTrash } from '@fortawesome/free-solid-svg-icons';
import { findWheelSnapshotsForPeriod, extractWheelBalanceData } from '../../../components/reflection/wheelSnapshotUtils';
import WheelSnapshotChart from '../../../components/reflection/WheelSnapshotChart';
import MeSnapshotComparison from '../../../components/me/MeSnapshotComparison';
import { findMeSnapshotsForPeriod } from '../../../components/me/meSnapshotUtils';
import { extractLifePartPeriodData, LifePartPeriodData } from '../../../components/reflection/lifePartSnapshotUtils';
import { calculateAverageRoleEfficiency } from '../../../components/reflection/roleEfficiencyUtils';
import LifePartReflectionBlock from '../../../components/reflection/LifePartReflectionBlock';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ReflectionSections } from '../../../state/ReflectionSections';
import useActualWheelId from '@/hooks/useActualWheelId';
import useWheelId from '@/hooks/useWheelId';
import { useReadonly } from '@/contexts/readonlyContext';

export default function ReflectionViewPage() {
    const navigate = useNavigate();
    const { reflectionUuid } = useParams<{ reflectionUuid: string }>();
    const pathWheelId = useWheelId();
    const wheelId = useActualWheelId();
    const { reflections, wheelSnapshots, conclusions, decisions, meSnapshots, roleEfficiencySnapshots } = useData();
    const { readonly } = useReadonly();

    const [isLoading, setIsLoading] = useState(true);
    const reflection = reflections.objects[reflectionUuid!];

    const allMeSnapshots = React.useMemo(() =>
        Object.values(meSnapshots.objects).filter((s: any) => s.wheelId === wheelId),
        [meSnapshots.objects, wheelId]
    );
    const allWheelSnapshots = React.useMemo(() =>
        Object.values(wheelSnapshots.objects).filter((s: any) => s.wheelId === wheelId),
        [wheelSnapshots.objects, wheelId]
    );
    const allRoleEfficiencySnapshots = React.useMemo(() =>
        Object.values(roleEfficiencySnapshots.objects).filter((s: any) => s.wheelId === wheelId),
        [roleEfficiencySnapshots.objects, wheelId]
    );

    const [initialWheel, setInitialWheel] = useState<any[]>([]);
    const [finalWheel, setFinalWheel] = useState<any[]>([]);
    const [initialMe, setInitialMe] = useState<any>(null);
    const [finalMe, setFinalMe] = useState<any>(null);
    const [lifePartsData, setLifePartsData] = useState<LifePartPeriodData[]>([]);
    const [conclusionList, setConclusionList] = useState<string[]>([]);
    const [decisionList, setDecisionList] = useState<string[]>([]);

    useEffect(() => {
        if (!reflection) {
            setIsLoading(false);
            return;
        }

        // Находим снимки колеса для периода
        const { initial: initialWheelSnapshot, final: finalWheelSnapshot } = findWheelSnapshotsForPeriod(
            allWheelSnapshots,
            parseISO(reflection.dateFrom),
            parseISO(reflection.dateTo)
        );

        setInitialWheel(extractWheelBalanceData(initialWheelSnapshot));
        setFinalWheel(extractWheelBalanceData(finalWheelSnapshot));

        // Находим снимки MeSnapshot для периода
        const { initial: initialMeSnapshot, final: finalMeSnapshot } = findMeSnapshotsForPeriod(
            allMeSnapshots,
            parseISO(reflection.dateFrom),
            parseISO(reflection.dateTo)
        );

        setInitialMe(initialMeSnapshot);
        setFinalMe(finalMeSnapshot);

        // Извлекаем данные LifePart для выбранных сфер
        const sections = reflection.sections as ReflectionSections | undefined;
        const lifepartUuids = sections?.lifeparts || [];

        // Вычисляем среднюю эффективность для каждой роли за период
        const roleNames = new Map<string, string>();
        const roleColors = new Map<string, string>();
        
        [...(initialWheelSnapshot?.lifeParts || []), 
          ...(finalWheelSnapshot?.lifeParts || [])].forEach(part => {
            roleNames.set(part.uuid, part.name);
            roleColors.set(part.uuid, part.color);
        });

        const efficiencyData = calculateAverageRoleEfficiency(
            allRoleEfficiencySnapshots,
            reflection.dateFrom,
            reflection.dateTo,
            roleNames,
            roleColors
        );

        const averageEfficiencies = new Map<string, number | null>();
        const efficiencySnapshotsCounts = new Map<string, number>();
        
        efficiencyData.forEach(data => {
            averageEfficiencies.set(data.uuid, data.averageEfficiency);
            efficiencySnapshotsCounts.set(data.uuid, data.snapshotsCount);
        });

        const lpData = extractLifePartPeriodData(
            initialWheelSnapshot,
            finalWheelSnapshot,
            lifepartUuids,
            averageEfficiencies,
            efficiencySnapshotsCounts,
        );
        setLifePartsData(lpData);

        // Загружаем выводы
        const reflectionConclusions = Object.values(conclusions.objects)
            .filter((c: any) => c.reflectionUuid === reflectionUuid)
            .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
            .map((c: any) => c.text);
        setConclusionList(reflectionConclusions);

        // Загружаем решения
        const reflectionDecisions = Object.values(decisions.objects)
            .filter((d: any) => d.reflectionUuid === reflectionUuid)
            .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
            .map((d: any) => ({ text: d.text, isDone: d.isDone }));
        setDecisionList(reflectionDecisions.map((d: any) => d.text));

        setIsLoading(false);
    }, [reflection, wheelId, wheelSnapshots, meSnapshots, conclusions, decisions, reflectionUuid]);

    const handleDelete = () => {
        if (readonly) return;
        if (window.confirm('Вы уверены, что хотите удалить эту запись рефлексии?')) {
            reflections.delete(reflectionUuid!);
            navigate(`/${pathWheelId}/reflection`);
        }
    };

    if (isLoading) {
        return (
            <div className="px-3 d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!reflection) {
        return (
            <div className="px-3">
                <Alert variant="danger">
                    Рефлексия не найдена
                </Alert>
                <Button variant="primary" onClick={() => navigate(`/${pathWheelId}/reflection`)}>
                    <Icon icon={faArrowLeft} className="me-2" />
                    Вернуться к списку
                </Button>
            </div>
        );
    }

    return (
        <div className="px-3 py-4">
            <Row className="mb-4">
                <Col>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => navigate(`/${pathWheelId}/reflection`)}
                        className="mb-3"
                    >
                        <Icon icon={faArrowLeft} className="me-2" />
                        Назад к списку
                    </Button>

                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <h3>{reflection.title || 'Без названия'}</h3>
                            <p className="text-muted">
                                {format(parseISO(reflection.dateFrom), 'd MMMM yyyy', { locale: ru })}
                                {' — '}
                                {format(parseISO(reflection.dateTo), 'd MMMM yyyy', { locale: ru })}
                            </p>
                        </div>
                        <Button variant="outline-danger" onClick={handleDelete}>
                            <Icon icon={faTrash} className="me-2" />
                            Удалить
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* Блок "Кто я и моя миссия" — только если включен */}
            {(reflection.sections as ReflectionSections)?.me && (
                <MeSnapshotComparison
                    initial={initialMe}
                    final={finalMe}
                    dateFrom={reflection.dateFrom}
                    dateTo={reflection.dateTo}
                />
            )}

            {/* Блок "Баланс" — только если включен */}
            {(reflection.sections as ReflectionSections)?.balance && (
                <>
                    <Row xs={1} md={2} className="g-3 mb-4">
                        <Col>
                            <Card className="h-100">
                                <Card.Header>
                                    <strong>
                                        <Icon icon={faChartPie} className="me-2" />
                                        Начало периода
                                    </strong>
                                    <div className="text-muted small">
                                        {format(parseISO(reflection.dateFrom), 'dd.MM.yyyy')}
                                    </div>
                                </Card.Header>
                                <Card.Body className="d-flex align-items-center justify-content-center">
                                    {!initialWheel || initialWheel.length === 0 ? (
                                        <Alert variant="info" className="mb-0 text-center">
                                            Нет данных<br />
                                        </Alert>
                                    ) : (
                                        <WheelSnapshotChart
                                            data={initialWheel}
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col>
                            <Card className="h-100">
                                <Card.Header>
                                    <strong>
                                        <Icon icon={faChartPie} className="me-2" />
                                        Конец периода
                                    </strong>
                                    <div className="text-muted small">
                                        {format(parseISO(reflection.dateTo), 'dd.MM.yyyy')}
                                    </div>
                                </Card.Header>
                                <Card.Body className="d-flex align-items-center justify-content-center">
                                    {!finalWheel || finalWheel.length === 0 ? (
                                        <Alert variant="info" className="mb-0 text-center">
                                            Нет данных
                                        </Alert>
                                    ) : (
                                        <WheelSnapshotChart
                                            data={finalWheel}
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            {/* Блоки LifePart — только если выбраны */}
            {(reflection.sections as ReflectionSections)?.lifeparts && (reflection.sections as ReflectionSections).lifeparts.length > 0 && (
                <>
                    {lifePartsData.map((lpData) => (
                        <Row key={lpData.uuid} className="mb-4">
                            <Col>
                                <LifePartReflectionBlock
                                    data={lpData}
                                    dateFrom={reflection.dateFrom}
                                    dateTo={reflection.dateTo}
                                />
                            </Col>
                        </Row>
                    ))}
                </>
            )}

            {/* Мысли */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <strong>Мысли</strong>
                        </Card.Header>
                        <Card.Body>
                            {reflection.thoughts ? (
                                <div className="markdown-content">
                                    <Markdown remarkPlugins={[remarkGfm]}>{reflection.thoughts}</Markdown>
                                </div>
                            ) : (
                                <em className="text-muted">Нет заметок</em>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Выводы */}
            {conclusionList.length > 0 && (
                <Row className="mb-4">
                    <Col>
                        <Card>
                            <Card.Header>
                                <strong>Выводы</strong>
                            </Card.Header>
                            <Card.Body>
                                <ul className="mb-0">
                                    {conclusionList.map((text, index) => (
                                        <li key={index} className="mb-2">{text}</li>
                                    ))}
                                </ul>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Решения */}
            {decisionList.length > 0 && (
                <Row className="mb-4">
                    <Col>
                        <Card>
                            <Card.Header>
                                <strong>Решения</strong>
                            </Card.Header>
                            <Card.Body>
                                <ul className="mb-0">
                                    {decisionList.map((text, index) => (
                                        <li key={index} className="mb-2">{text}</li>
                                    ))}
                                </ul>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
}
